<template>
  <LayoutCard :title="`Application Form Status - ${form.published ? 'Published' : 'Not Published'}`">
    <template #description>
      <template v-if="form.published">
        The license application form is currently accessible to the public at
        <a :href="safeHref(form.url)" target="_blank" rel="noopener noreferrer">{{ form.url }} <i class="bi-box-arrow-up-right"/></a>. Unpublishing it will hide it from public view.
      </template>
      <template v-else>
        The license application form is currently hidden from public view. Publishing it will make it accessible to the public.
      </template>
    </template>
    <button type="button" :class="`btn btn-${form.published ? 'outline-primary' : 'primary'}`" @click="togglePublished" :disabled="form.publishing">
      <spinner-view small v-if="form.publishing" /> {{ form.published ? 'Unpublish' : 'Publish' }}
    </button>
  </LayoutCard>

  <LayoutCard title="Application Payment Message">
    <template #description>
      After a user submits a license application, they will see a confirmation message that instructs them how to pay
      for their license. You may optionally specify a direct payment link that will be presented as a clear call to
      action, below the confirmation message.
    </template>
    <template #footer v-if="confirmationTextChanged">
      <button type="button" class="btn btn-flat ms-1" @click="resetConfirmation">
        Cancel
      </button>
      <button type="button" class="btn btn-primary ms-1" @click="commitConfirmation">
        Save Changes
      </button>
    </template>
    <div>
      <label class="form-label">Confirmation message</label>
      <textarea class="form-control" v-model="confirmationText" rows="4" />
    </div>
    <div>
      <label class="form-label">Payment Link</label>
      <input type="url" class="form-control" v-model="paymentUrl" />
    </div>
    <div>
      <label class="form-label">Payment Link Text</label>
      <input type="url" class="form-control" v-model="paymentLabel" placeholder="Pay here" />
    </div>
  </LayoutCard>

  <LayoutCard title="Application Email Notifications">
    <template #description>
      When a user submits a license application, these staff addresses will be BCC'd on the confirmation email sent
      to the applicant. Leave this blank to disable staff notifications.
    </template>
    <template #footer v-if="staffNotificationEmailsChanged">
      <button type="button" class="btn btn-flat ms-1" @click="resetStaffNotificationEmails">
        Cancel
      </button>
      <button type="button" class="btn btn-primary ms-1" @click="commitStaffNotificationEmails">
        Save Changes
      </button>
    </template>
    <div>
      <label class="form-label">Staff notification email addresses</label>
      <textarea
        class="form-control"
        v-model="staffNotificationEmailsText"
        rows="4"
        placeholder="staff@example.gov&#10;permits@example.gov" />
      <div class="form-text">Separate multiple addresses with commas or new lines.</div>
    </div>
  </LayoutCard>

  <LayoutCard title="Application Custom Fields">

    <template #description>
      By default, we ask applicants for the property address, contact information, and links to relevant short-term
      rental listings. You can also customize your own fields to gather additional information required for your
      licenses.
    </template>

    <template v-if="form" #flush>
      <LicenseApplicationFormEditor :form="form" />
    </template>
  </LayoutCard>
</template>

<script>
import { safeHref } from '@/safeRendering';
import LayoutCard from "@/components/common/LayoutCard.vue";
import {City} from "@/models/City";
import SpinnerView from "@/components/common/SpinnerView.vue";
import {LicenseApplicationForm} from "@/models/LicenseApplicationForm";
import LicenseApplicationFormEditor from "@/components/admin-city/licenses/LicenseApplicationFormEditor.vue";

function formatEmailText(emails) {
  return (emails || []).join('\n');
}

export default {
  components: {LicenseApplicationFormEditor, SpinnerView, LayoutCard},
  props: {
    city: City
  },
  data() {
    return {
      form: null,
      confirmationText: null,
      paymentUrl: null,
      paymentLabel: null,
      staffNotificationEmailsText: ''
    };
  },
  watch: {
    city: {
      immediate: true,
      handler() {
        console.log(`Building application form`);
        this.form = new LicenseApplicationForm(this.city);
        this.confirmationText = this.city.licenseApplicationConfirmationText;
        this.staffNotificationEmailsText = formatEmailText(this.city.licenseApplicationStaffNotificationEmails);
        console.log(`Done building application form`);
      }
    }
  },
  computed: {
    confirmationTextChanged() {
      return this.confirmationText !== this.city.licenseApplicationConfirmationText
        || this.paymentUrl !== this.city.licenseApplicationPaymentUrl
        || this.paymentLabel !== this.city.licenseApplicationPaymentLabel;
    },
    staffNotificationEmailsChanged() {
      return this.staffNotificationEmailsText !== formatEmailText(this.city.licenseApplicationStaffNotificationEmails);
    },
    changed() {
      return true;
    }
  },
  methods: {
    safeHref,

    reset() {
      this.form.reset();
    },
    commit() {
      this.form.commit();
    },
    togglePublished() {
      this.form.updatePublished(!this.form.published);
    },
    resetConfirmation() {
      this.confirmationText = this.city.licenseApplicationConfirmationText;
      this.paymentUrl = this.city.licenseApplicationPaymentUrl;
      this.paymentLabel = this.city.licenseApplicationPaymentLabel;
    },
    resetStaffNotificationEmails() {
      this.staffNotificationEmailsText = formatEmailText(this.city.licenseApplicationStaffNotificationEmails);
    },
    async commitConfirmation() {
      await this.city.updateLicenseApplicationConfirmation({
        confirmationText: this.confirmationText,
        paymentUrl: this.paymentUrl,
        paymentLabel: this.paymentLabel
      });
      this.$showToast('Payment confirmation message and link saved');
    },
    async commitStaffNotificationEmails() {
      await this.city.updateLicenseApplicationStaffNotificationEmails(this.staffNotificationEmailsText);
      this.staffNotificationEmailsText = formatEmailText(this.city.licenseApplicationStaffNotificationEmails);
      this.$showToast('Staff notification emails saved');
    },
  }
}
</script>
